import {
	useGetAcceptedMeditatorByRoom,
	useMoveMeditatorToWaitingRoom,
} from "@/hooks/query-hooks/useRoom";
import { useDebounce } from "@/hooks/useDebounce";
import {
	CloseOutlined,
	ExclamationCircleOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import { Button, Card, Empty, Input, Modal, Pagination, Space } from "antd";
import { useMemo, useState } from "react";
import { UserCard } from "./UserCard";

const PAGE_SIZE = 10;

export function AcceptedRoomRequests(props: Room) {
	const { id, count } = props;
	const [modal, contextHolder] = Modal.useModal();
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [keyword, setKeyword] = useState<string | undefined>();
	const debouncedValue = useDebounce(keyword, 250);

	const { data: acceptedRequests = [], isLoading } =
		useGetAcceptedMeditatorByRoom(id, count > 0);

	const paginatedData = useMemo(() => {
		if (debouncedValue && debouncedValue.trim().length > 0) {
			return acceptedRequests?.filter(
				(it) =>
					it.userName
						.toLowerCase()
						.includes(debouncedValue.toLowerCase()) ||
					it.bookNo
						?.toLowerCase()
						.includes(debouncedValue.toLowerCase()),
			);
		}
		return acceptedRequests?.slice(
			currentPage - 1,
			currentPage * PAGE_SIZE,
		);
	}, [currentPage, acceptedRequests, debouncedValue]);

	const { mutate } = useMoveMeditatorToWaitingRoom();

	const handleConfirm = (user: RoomRequestUser) => {
		const { userName, userId, bookNo } = user;
		modal.confirm({
			icon: <ExclamationCircleOutlined className="text-danger!" />,
			title: (
				<h2 className="font-poppins text-danger">
					Move {userName} to waiting room?
				</h2>
			),
			content: (
				<p className="font-poppins">
					Are you sure you want to move {userName}{" "}
					{bookNo ? `(${bookNo})` : null} back to waiting room?
				</p>
			),
			okText: "Confirm",
			okButtonProps: {
				type: "primary",
				className: "rounded-lg!",
				danger: true,
			},
			cancelButtonProps: {
				type: "text",
			},
			onOk: () => {
				return new Promise((resolve, reject) => {
					mutate(
						{ roomId: id, userId },
						{
							onSuccess: resolve,
							onError: reject,
						},
					);
				});
			},
		});
	};

	return (
		<>
			{contextHolder}
			<Card
				title={
					<h2 className="font-poppins">
						Meditators ({acceptedRequests?.length})
					</h2>
				}
				loading={isLoading}
				extra={[
					<Input
						prefix={<SearchOutlined />}
						size="large"
						style={{
							width: 300,
						}}
						placeholder="Search people in this room . . ."
						value={keyword}
						onChange={(e) => setKeyword(e.target.value)}
						allowClear
						key={"search"}
					/>,
				]}
			>
				{paginatedData?.length > 0 ? (
					<Space direction="vertical" size="large" className="w-full">
						{paginatedData.map((it, idx) => (
							<UserCard
								key={it.id}
								user={it}
								index={idx + 1}
								extra={
									<Button
										shape="circle"
										icon={<CloseOutlined />}
										type="primary"
										danger
										title="Cancel"
										style={{
											backgroundColor: "#FEF2F2",
										}}
										className="aspect-square! text-danger!"
										onClick={() => handleConfirm(it)}
									/>
								}
							/>
						))}
						{paginatedData?.length > 0 && (
							<Space className="w-full! justify-end">
								<Pagination
									pageSize={PAGE_SIZE}
									defaultCurrent={1}
									total={
										debouncedValue &&
										acceptedRequests?.length > 0
											? paginatedData?.length
											: acceptedRequests?.length
									}
									current={currentPage}
									onChange={(page) => setCurrentPage(page)}
								/>
							</Space>
						)}
					</Space>
				) : debouncedValue &&
				  paginatedData?.length === 0 &&
				  acceptedRequests?.length > 0 ? (
					<Empty
						description={`No accepted meditators found with ${debouncedValue}!`}
						classNames={{
							description: "font-poppins",
						}}
					/>
				) : (
					<Empty
						description="No accepted meditators yet!"
						classNames={{
							description: "font-poppins",
						}}
					/>
				)}
			</Card>
		</>
	);
}
